import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Reporting-Modul (Stakeholder + Buchhaltung): vier read-only Auswertungen.
 * Alle Geldbeträge als Money (bigint-Mikro-Einheiten, string-serialisiert) —
 * §6, keine Float-Summen.
 */

export const reportingYearQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type ReportingYearQuery = z.infer<typeof reportingYearQuerySchema>;

// 1) Auftragseingang je Monat
/** Einzelner Auftrag (Beleg), der zum Monatswert beiträgt — für die Aufgliederung. */
export const orderIntakeOrderSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  documentDate: z.string().datetime(),
  net: moneySchema,
  /** Netto − EK-Kosten der Artikelpositionen, approximiert über den aktuellen
   * EK-Preis je Artikel (kein historischer Kosten-Snapshot je Position). */
  grossMargin: moneySchema,
  /** Ausstellende Gesellschaft (Name-Snapshot am Beleg, ADR 0029). */
  legalEntityName: z.string().nullable(),
});
export type OrderIntakeOrder = z.infer<typeof orderIntakeOrderSchema>;

export const orderIntakeMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  monthLabel: z.string(),
  count: z.number().int(),
  net: moneySchema,
  grossMargin: moneySchema,
  /** Die einzelnen Aufträge dieses Monats. */
  orders: z.array(orderIntakeOrderSchema),
});
export type OrderIntakeMonth = z.infer<typeof orderIntakeMonthSchema>;

export const orderIntakeReportSchema = z.object({
  year: z.number().int(),
  currency: z.enum(['EUR', 'USD', 'CHF']),
  months: z.array(orderIntakeMonthSchema),
  totalCount: z.number().int(),
  totalNet: moneySchema,
  totalGrossMargin: moneySchema,
});
export type OrderIntakeReport = z.infer<typeof orderIntakeReportSchema>;

// 2) Umsatz nach Kunde & Land
export const revenueByCustomerSchema = z.object({
  customerId: z.string().uuid().nullable(),
  customerName: z.string(),
  country: z.string().nullable(),
  net: moneySchema,
  invoiceCount: z.number().int(),
});
export type RevenueByCustomer = z.infer<typeof revenueByCustomerSchema>;

export const revenueByCountrySchema = z.object({
  country: z.string(),
  net: moneySchema,
  invoiceCount: z.number().int(),
});
export type RevenueByCountry = z.infer<typeof revenueByCountrySchema>;

export const revenueReportSchema = z.object({
  year: z.number().int(),
  currency: z.enum(['EUR', 'USD', 'CHF']),
  byCustomer: z.array(revenueByCustomerSchema),
  byCountry: z.array(revenueByCountrySchema),
  totalNet: moneySchema,
});
export type RevenueReport = z.infer<typeof revenueReportSchema>;

// 3) Lagerwert
export const inventoryValueRowSchema = z.object({
  articleId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  stockQty: z.number(),
  unitPrice: moneySchema,
  value: moneySchema,
});
export type InventoryValueRow = z.infer<typeof inventoryValueRowSchema>;

export const inventoryValueReportSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  rows: z.array(inventoryValueRowSchema),
  totalValue: moneySchema,
});
export type InventoryValueReport = z.infer<typeof inventoryValueReportSchema>;

// 4) HF/NAL — halbfertige / nicht abrechenbare Leistungen (Kosten − Fakturiert je Projekt)
export const wipRowSchema = z.object({
  projectId: z.string().uuid(),
  projectCode: z.string(),
  projectName: z.string(),
  cost: moneySchema,
  invoiced: moneySchema,
  wip: moneySchema,
});
export type WipRow = z.infer<typeof wipRowSchema>;

export const wipReportSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  rows: z.array(wipRowSchema),
  totalCost: moneySchema,
  totalInvoiced: moneySchema,
  totalWip: moneySchema,
});
export type WipReport = z.infer<typeof wipReportSchema>;

/**
 * HF/NAL ist teuer (je Projekt eine Wirtschaftlichkeits-Aggregation), daher
 * seitenweise über die AKTIVEN Projekte (Status ≠ GESCHLOSSEN/STORNIERT),
 * alphabetisch nach Projekt-Code. Default 25 Zeilen je Seite (§8).
 */
export const wipQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(25),
});
export type WipQuery = z.infer<typeof wipQuerySchema>;

export const wipReportPageSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  data: z.array(wipRowSchema),
  meta: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  }),
  /** Summen NUR der aktuellen Seite — nicht des Gesamtbestands (es werden je Seite nur pageSize Projekte berechnet). */
  pageTotals: z.object({
    cost: moneySchema,
    invoiced: moneySchema,
    wip: moneySchema,
  }),
});
export type WipReportPage = z.infer<typeof wipReportPageSchema>;
